import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Loader2 } from 'lucide-react'
import type { CampoFormulario } from '../types'

const PROVINCIAS_ECUADOR = [
  'Azuay', 'Bolívar', 'Cañar', 'Carchi', 'Chimborazo', 'Cotopaxi',
  'El Oro', 'Esmeraldas', 'Galápagos', 'Guayas', 'Imbabura', 'Loja',
  'Los Ríos', 'Manabí', 'Morona Santiago', 'Napo', 'Orellana',
  'Pastaza', 'Pichincha', 'Santa Elena', 'Santo Domingo de los Tsáchilas',
  'Sucumbíos', 'Tungurahua', 'Zamora Chinchipe',
]

interface DynamicFormProps {
  campos: CampoFormulario[]
  onSubmit: (datos: Record<string, string>) => void
  isSubmitting?: boolean
  submitLabel?: string
}

export function DynamicForm({ campos, onSubmit, isSubmitting, submitLabel = 'Enviar' }: DynamicFormProps) {
  const [values, setValues] = useState<Record<string, string>>({})
  const [errors, setErrors] = useState<Record<string, string>>({})

  const setValue = (name: string, value: string) => {
    setValues(prev => ({ ...prev, [name]: value }))
    if (errors[name]) {
      setErrors(prev => {
        const next = { ...prev }
        delete next[name]
        return next
      })
    }
  }

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {}
    for (const campo of campos) {
      const val = values[campo.nombre_campo]?.trim() || ''
      if (campo.requerido && !val) {
        newErrors[campo.nombre_campo] = `${campo.etiqueta} es obligatorio`
        continue
      }
      if (val && campo.tipo === 'email') {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
        if (!emailRegex.test(val)) {
          newErrors[campo.nombre_campo] = 'Ingrese un email válido'
        }
      }
      if (val && campo.tipo === 'tel') {
        const phoneRegex = /^09\d{8}$/
        const clean = val.replace(/[^0-9]/g, '')
        if (!phoneRegex.test(clean)) {
          newErrors[campo.nombre_campo] = 'Ingrese un número válido (ej: 0990350515)'
        }
      }
      if (val && campo.min_length && val.length < campo.min_length) {
        newErrors[campo.nombre_campo] = `Mínimo ${campo.min_length} caracteres`
      }
      if (val && campo.max_length && val.length > campo.max_length) {
        newErrors[campo.nombre_campo] = `Máximo ${campo.max_length} caracteres`
      }
    }
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (validate()) {
      onSubmit(values)
    }
  }

  const renderField = (campo: CampoFormulario) => {
    const value = values[campo.nombre_campo] || ''
    const error = errors[campo.nombre_campo]

    switch (campo.tipo) {
      case 'select':
        return (
          <Select value={value} onValueChange={(v) => setValue(campo.nombre_campo, v)}>
            <SelectTrigger className={error ? 'border-red-500' : ''}>
              <SelectValue placeholder={campo.placeholder || 'Seleccionar...'} />
            </SelectTrigger>
            <SelectContent>
              {campo.opciones.map((opt) => (
                <SelectItem key={opt} value={opt}>{opt}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )

      case 'provincia':
        return (
          <Select value={value} onValueChange={(v) => setValue(campo.nombre_campo, v)}>
            <SelectTrigger className={error ? 'border-red-500' : ''}>
              <SelectValue placeholder="Seleccionar provincia..." />
            </SelectTrigger>
            <SelectContent>
              {PROVINCIAS_ECUADOR.map((prov) => (
                <SelectItem key={prov} value={prov}>{prov}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )

      case 'radio':
        return (
          <div className="space-y-2">
            {campo.opciones.map((opt) => (
              <label key={opt} className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="radio"
                  name={campo.nombre_campo}
                  value={opt}
                  checked={value === opt}
                  onChange={() => setValue(campo.nombre_campo, opt)}
                />
                {opt}
              </label>
            ))}
          </div>
        )

      case 'checkbox':
        return (
          <div className="space-y-2">
            {campo.opciones.map((opt) => (
              <label key={opt} className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={value.split(',').includes(opt)}
                  onChange={(e) => {
                    const current = value ? value.split(',') : []
                    const next = e.target.checked
                      ? [...current, opt]
                      : current.filter(v => v !== opt)
                    setValue(campo.nombre_campo, next.join(','))
                  }}
                />
                {opt}
              </label>
            ))}
          </div>
        )

      case 'checkbox_terminos':
        return (
          <label className="flex items-start gap-2 text-sm cursor-pointer">
            <input
              type="checkbox"
              checked={value === '1'}
              onChange={(e) => setValue(campo.nombre_campo, e.target.checked ? '1' : '')}
              className="mt-1"
            />
            <span>{campo.etiqueta}</span>
          </label>
        )

      case 'textarea':
        return (
          <textarea
            className={`w-full border rounded-md p-2 text-sm min-h-[80px] bg-background ${error ? 'border-red-500' : ''}`}
            value={value}
            onChange={(e) => setValue(campo.nombre_campo, e.target.value)}
            placeholder={campo.placeholder}
          />
        )

      default:
        return (
          <Input
            type={campo.tipo === 'tel' ? 'tel' : campo.tipo === 'email' ? 'email' : campo.tipo === 'number' ? 'number' : campo.tipo === 'date' ? 'date' : 'text'}
            value={value}
            onChange={(e) => setValue(campo.nombre_campo, e.target.value)}
            placeholder={campo.placeholder}
            className={error ? 'border-red-500' : ''}
          />
        )
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {campos.map((campo) => (
        <div key={campo.id} className="space-y-1.5">
          {campo.tipo !== 'checkbox_terminos' && (
            <Label className="text-sm">
              {campo.etiqueta}
              {campo.requerido && <span className="text-red-500 ml-1">*</span>}
            </Label>
          )}
          {renderField(campo)}
          {errors[campo.nombre_campo] && (
            <p className="text-xs text-red-500">{errors[campo.nombre_campo]}</p>
          )}
        </div>
      ))}

      <Button
        type="submit"
        className="w-full bg-teal-800 hover:bg-teal-900 text-white"
        disabled={isSubmitting}
      >
        {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
        {submitLabel}
      </Button>
    </form>
  )
}
